import { brandAssets } from '@echo/brand';
import logo from '@echo/brand/assets/logo-lockup-goodwin.png';
import { readCoreErrorCode } from '@echo/core';
import { useSessionManager } from '@echo/core/react';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { savePendingFlow, takePendingSocialLogin } from '@/lib/social-login';

interface CallbackSearch {
  code?: string;
  flow?: string;
}

export const Route = createFileRoute('/auth/callback')({
  validateSearch: (search: Record<string, unknown>): CallbackSearch => ({
    code: typeof search.code === 'string' ? search.code : undefined,
    flow: typeof search.flow === 'string' ? search.flow : undefined,
  }),
  component: AuthCallbackPage,
});

// StrictMode runs the effect below twice in development, and the pending login can be taken
// from storage only once: the second run found nothing and showed an error while the first
// was still signing in. Both runs share one exchange per callback URL.
const exchanges = new Map<string, Promise<void>>();

/**
 * Where Apple/Google send the browser back (`/auth/callback?code=…`, or `?flow=…` when the
 * provider's email already belongs to an OnEarly account). Finishes the login started on
 * the sign-in page and moves on; the page itself is only ever seen for a moment.
 */
function AuthCallbackPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const sessionManager = useSessionManager();
  const { code, flow } = Route.useSearch();
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    const finish = async () => {
      const pending = takePendingSocialLogin();
      if (!pending) throw new Error('No sign-in in progress');
      if (flow) {
        const linking = await sessionManager.provider.continueSocialLogin({ flowId: flow });
        savePendingFlow({ provider: pending.provider, flow: linking });
        await navigate({ to: '/sign-in', replace: true });
        return;
      }
      if (!code) throw new Error('No code in the callback');
      await sessionManager.signIn(
        await sessionManager.provider.completeSocialLogin({
          sessionTokenExchangeCode: pending.sessionTokenExchangeCode,
          returnToCode: code,
        }),
      );
      await navigate({ to: '/', replace: true });
    };
    const key = `${code ?? ''}|${flow ?? ''}`;
    let exchange = exchanges.get(key);
    if (!exchange) {
      exchange = finish();
      exchanges.set(key, exchange);
    }
    exchange.catch((e: unknown) => {
      setError(
        readCoreErrorCode(e) === 'SOCIAL_LINK_UNSUPPORTED'
          ? t`That account can't be linked here yet. Sign in with your phone or email instead.`
          : t`We couldn't finish signing you in. Please try again.`,
      );
    });
  }, [code, flow, navigate, sessionManager, t]);

  // The moment between the provider and Home is the sign-in page's own ground and lockup,
  // with a spinner where the form was, so it never reads as a broken white page.
  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-[480px] flex-col items-center gap-content-lg px-content-md pt-section-sm pb-content-lg">
      <img
        src={logo}
        alt="Stronger Memory"
        width={brandAssets.logoLockupGoodwin.width}
        height={brandAssets.logoLockupGoodwin.height}
        className="h-auto w-[180px] self-start"
      />
      <div className="flex flex-1 flex-col items-center justify-center gap-content-sm text-center">
        {error ? (
          <>
            <p role="alert" className="text-body-md text-status-error">
              {error}
            </p>
            <Link to="/sign-in" className="text-body-md text-on-action-secondary underline">
              <Trans>Back to sign in</Trans>
            </Link>
          </>
        ) : (
          <>
            <span
              aria-hidden
              className="size-10 animate-spin rounded-full border-4 border-action-onearly border-t-transparent"
            />
            <p className="text-body-md text-on-surface-medium" aria-live="polite">
              <Trans>Signing you in…</Trans>
            </p>
          </>
        )}
      </div>
    </main>
  );
}
